import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  FaCalendarAlt,
  FaClock,
  FaVideo,
  FaGlobe,
  FaLanguage,
  FaUsers,
  FaCheckCircle,
  FaArrowLeft,
  FaUserTie,
  FaBookOpen,
  FaGraduationCap,
  FaListUl,
  FaExclamationTriangle
} from "react-icons/fa";
import { getApiUrl } from "../../utils/api";
import { formatDisplay12Hour } from "../../utils/eventTimeUtils";
import ShareEventButton from "../../components/ShareEventButton/ShareEventButton";
import "./EventDetail.css";

const EventDetail = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEventDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(getApiUrl(`/events/${slug}`));
      const data = await res.json();

      if (data.success && data.event) {
        setEvent(data.event);
      } else {
        setError(data.message || "Event not found");
      }
    } catch (err) {
      console.error("❌ Fetch Event Detail Error:", err);
      setError("Network error loading event details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchEventDetails();
  }, [fetchEventDetails]);

  if (loading) {
    return (
      <div className="event-detail-page-container">
        <div className="event-detail-loading">
          <div className="events-spinner" />
          <p>Loading event information...</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-detail-page-container">
        <div className="event-detail-not-found">
          <FaExclamationTriangle className="error-icon" />
          <h2>Event Not Found</h2>
          <p>{error || "The event you are looking for does not exist or is not available for public registration."}</p>
          <Link to="/events" className="back-to-events-btn">
            <FaArrowLeft /> Back to Events
          </Link>
        </div>
      </div>
    );
  }

  const total = event.totalSeats || 0;
  const booked = event.bookedSeats || 0;
  const remaining = Math.max(0, total - booked);
  const percentage = total > 0 ? Math.min(100, Math.round((booked / total) * 100)) : 0;
  const isFullyBooked = remaining === 0;
  const isPast = event.eventStartAt && new Date() >= new Date(event.eventStartAt);

  return (
    <div className="event-detail-page-container">
      {/* Top Breadcrumb & Navigation */}
      <div className="event-breadcrumb-row">
        <Link to="/events" className="breadcrumb-back-link">
          <FaArrowLeft /> All Events & Sessions
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">{event.title}</span>
      </div>

      {/* HERO BANNER SECTION */}
      <section className="event-detail-hero">
        <div className="event-hero-banner-container">
          <img
            src={event.bannerImage || event.eventBannerUrl || event.bannerImageUrl}
            alt={event.title}
            className="event-hero-banner-img"
          />
          <div className="event-hero-overlay" />
          <div className="event-hero-overlay-content">
            <div className="event-hero-badges-row">
              <span className="hero-category-badge">{event.category}</span>
              {isFullyBooked ? (
                <span className="hero-status-badge fully-booked">Fully Booked</span>
              ) : isPast ? (
                <span className="hero-status-badge past">Event Ended</span>
              ) : (
                <span className="hero-status-badge open">Registration Open</span>
              )}
            </div>
            <h1 className="event-hero-title">{event.title}</h1>
            <p className="event-hero-lead">{event.shortDescription}</p>

            {/* Tags */}
            {event.tags && event.tags.length > 0 && (
              <div className="event-hero-tags">
                {event.tags.map((tag, idx) => (
                  <span key={idx} className="event-tag-pill">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* MAIN TWO-COLUMN CONTENT LAYOUT */}
      <div className="event-content-layout">
        {/* LEFT COLUMN: EVENT DETAILS & PROGRAM */}
        <main className="event-main-details">
          {/* About Event / Long Description */}
          <section className="event-section-card">
            <h2 className="section-title">
              <FaBookOpen className="section-icon" /> About This Event
            </h2>
            <div className="event-long-description">
              {event.longDescription.split("\n\n").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </section>

          {/* What You'll Learn */}
          {event.learningOutcomes && event.learningOutcomes.length > 0 && (
            <section className="event-section-card">
              <h2 className="section-title">
                <FaGraduationCap className="section-icon" /> What You'll Learn
              </h2>
              <ul className="learning-outcomes-list">
                {event.learningOutcomes.map((item, index) => (
                  <li key={index} className="learning-outcome-item">
                    <FaCheckCircle className="check-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Target Audience & Prerequisites */}
          <div className="event-grid-two-cards">
            {event.targetAudience && (
              <section className="event-section-card compact">
                <h3 className="section-subtitle">
                  <FaUsers className="section-icon" /> Who Should Attend?
                </h3>
                <p className="card-text-muted">{event.targetAudience}</p>
              </section>
            )}

            {event.prerequisites && (
              <section className="event-section-card compact">
                <h3 className="section-subtitle">
                  <FaCheckCircle className="section-icon" /> Prerequisites
                </h3>
                <p className="card-text-muted">{event.prerequisites}</p>
              </section>
            )}
          </div>

          {/* Event Agenda */}
          {event.agenda && event.agenda.length > 0 && (
            <section className="event-section-card">
              <h2 className="section-title">
                <FaListUl className="section-icon" /> Event Agenda
              </h2>
              <div className="agenda-timeline">
                {event.agenda.map((item, index) => (
                  <div key={index} className="agenda-item">
                    <div className="agenda-time-pill">{item.time || `Session ${index + 1}`}</div>
                    <div className="agenda-content">
                      <h4 className="agenda-item-title">{item.title}</h4>
                      {item.description && <p className="agenda-item-desc">{item.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Host / Speaker Section */}
          {event.hostInformation && (
            <section className="event-section-card host-card">
              <h2 className="section-title">
                <FaUserTie className="section-icon" /> Event Host & Speaker
              </h2>
              <div className="host-info-container">
                <div className="host-avatar">
                  <img
                    src="https://res.cloudinary.com/dxbwhvybv/image/upload/v1774941428/Praveen_Kumar_hp4ucf.png"
                    alt={event.hostInformation.name}
                  />
                </div>
                <div className="host-details">
                  <h3 className="host-name">{event.hostInformation.name}</h3>
                  <p className="host-designation">{event.hostInformation.designation}</p>
                  <p className="host-bio">{event.hostInformation.bio}</p>
                </div>
              </div>
            </section>
          )}

          {/* Social Sharing Section */}
          <section className="event-section-card share-card">
            <h2 className="section-title">Share This Event</h2>
            <p className="share-card-subtitle">
              Know someone who would benefit from this session? Share with your friends, colleagues, and developer network.
            </p>
            <div className="share-button-container">
              <ShareEventButton event={event} />
            </div>
          </section>
        </main>

        {/* RIGHT COLUMN: STICKY BOOKING & METRIC SIDEBAR */}
        <aside className="event-sidebar">
          <div className="event-sidebar-sticky-card">
            {/* Seat Availability Progress Card */}
            <div className="sidebar-seat-box">
              <div className="sidebar-seat-header">
                <span className="sidebar-seat-title">
                  <FaUsers /> Seat Capacity
                </span>
                <span className={`sidebar-seat-count ${isFullyBooked ? "full" : ""}`}>
                  {isFullyBooked ? (
                    <b>0 Seats Left</b>
                  ) : (
                    <>
                      <b>{remaining}</b> / {total} Available
                    </>
                  )}
                </span>
              </div>
              <div className="sidebar-progress-track">
                <div
                  className={`sidebar-progress-fill ${isFullyBooked ? "full" : ""}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="sidebar-seat-percentage">{percentage}% Capacity Filled</span>
            </div>

            {/* ACTION BOOK BUTTON */}
            <div className="sidebar-cta-container">
              {isFullyBooked ? (
                <>
                  <button
                    type="button"
                    className="event-book-action-btn disabled"
                    disabled
                  >
                    All Seats Filled - Registration Closed
                  </button>
                  <p className="booking-status-message error">
                    Sorry! All seats for this event have been booked.
                  </p>
                </>
              ) : isPast ? (
                <>
                  <button
                    type="button"
                    className="event-book-action-btn disabled"
                    disabled
                  >
                    Event Scheduled Time Has Passed
                  </button>
                  <p className="booking-status-message warning">
                    Registration has ended as this event is already underway or concluded.
                  </p>
                </>
              ) : (
                <>
                  <a
                    href={`/events/eventdetail/${event.slug}/book`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="event-book-action-btn active"
                  >
                    <span>Book Event →</span>
                  </a>
                  <p className="booking-status-message success">
                    Instant confirmation & private meeting link delivered directly to your inbox.
                  </p>
                </>
              )}
            </div>

            {/* Event Key Facts Table */}
            <div className="sidebar-meta-details">
              <h4 className="sidebar-meta-title">Event Overview</h4>

              <div className="sidebar-meta-item">
                <span className="sidebar-meta-label">
                  <FaCalendarAlt className="meta-icon" /> Date
                </span>
                <span className="sidebar-meta-val">{event.eventDate}</span>
              </div>

              <div className="sidebar-meta-item">
                <span className="sidebar-meta-label">
                  <FaClock className="meta-icon" /> Time (IST)
                </span>
                <span className="sidebar-meta-val">
                  {formatDisplay12Hour(event.startTime)} – {formatDisplay12Hour(event.endTime)}
                </span>
              </div>

              <div className="sidebar-meta-item">
                <span className="sidebar-meta-label">
                  <FaGlobe className="meta-icon" /> Timezone
                </span>
                <span className="sidebar-meta-val">{event.timezone || "Asia/Kolkata (GMT+05:30)"}</span>
              </div>

              <div className="sidebar-meta-item">
                <span className="sidebar-meta-label">
                  <FaClock className="meta-icon" /> Duration
                </span>
                <span className="sidebar-meta-val">{event.durationMinutes} Minutes</span>
              </div>

              <div className="sidebar-meta-item">
                <span className="sidebar-meta-label">
                  <FaGlobe className="meta-icon" /> Format
                </span>
                <span className="sidebar-meta-val">{event.format || "Live Online"}</span>
              </div>

              <div className="sidebar-meta-item">
                <span className="sidebar-meta-label">
                  <FaVideo className="meta-icon" /> Platform
                </span>
                <span className="sidebar-meta-val platform-highlight">
                  {event.meetingPlatform || "Google Meet"}
                </span>
              </div>

              <div className="sidebar-meta-item">
                <span className="sidebar-meta-label">
                  <FaLanguage className="meta-icon" /> Language
                </span>
                <span className="sidebar-meta-val">{event.language || "English"}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default EventDetail;
